import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import logger from '@/lib/logging';
import { Shield, FlaskConical, RotateCcw, AlertTriangle, Bug, Sparkles } from "lucide-react";
import { useSettings } from "./lib/settings-context";
import {
    POSTURE_ORIENTATION_STRATEGY_OPTIONS,
    DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID,
    resolvePostureOrientationStrategyId,
    type PostureOrientationStrategyId,
} from "@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id";
import { useAdmin } from "@/contexts/AdminContext";
import { useToast } from "@/hooks/use-toast";
import { useDataWithLoading } from "@/hooks/use-drill-data";
import UnifiedThemeSettings from "./UnifiedThemeSettings";
import SubscriptionBillingCard from "./SubscriptionBillingCard";
import { getAppSettings, updateAppSettings } from "@/components/feedback-button/lib/env-service";
import { clearSettingsCache } from "@/lib/auth-debug";

export default function SettingsContent() {
    const { settings, setSettings, unit, setUnit } = useSettings();
    const { isAdminMode, isAuthorizedAdmin, toggleAdminMode } = useAdmin();
    const { toast } = useToast();

    // App-wide picture saving setting
    const [saveSelfAssessmentPictures, setSaveSelfAssessmentPictures] = useState(true);
    const [isSavingPictureSetting, setIsSavingPictureSetting] = useState(false);

    // Diagnostic logging state (admin-only app-wide setting)
    const [diagnosticLoggingEnabled, setDiagnosticLoggingEnabled] = useState(false);
    const [clientLogLevel, setClientLogLevel] = useState<'debug' | 'info' | 'warn' | 'error'>('info');
    const [serverLogLevel, setServerLogLevel] = useState<'debug' | 'info' | 'warn' | 'error'>('info');
    const [isLoadingDiagnosticSetting, setIsLoadingDiagnosticSetting] = useState(false);
    const [isSavingDiagnosticSetting, setIsSavingDiagnosticSetting] = useState(false);

    // Fetch diagnostic logging & picture saving settings on mount
    useEffect(() => {
        const fetchAppSettings = async () => {
            setIsLoadingDiagnosticSetting(true);
            try {
                const appSettings = await getAppSettings();
                setDiagnosticLoggingEnabled(appSettings?.diagnosticLoggingEnabled ?? appSettings?.authDebugLoggingEnabled ?? false);
                setClientLogLevel(appSettings?.clientLogLevel ?? 'info');
                setServerLogLevel(appSettings?.serverLogLevel ?? 'info');
                if (appSettings?.saveSelfAssessmentPictures !== undefined) {
                    setSaveSelfAssessmentPictures(appSettings.saveSelfAssessmentPictures);
                }
            } catch (error) {
                logger.error('Error fetching diagnostic logging setting:', error);
            } finally {
                setIsLoadingDiagnosticSetting(false);
            }
        };

        fetchAppSettings();
    }, [isAuthorizedAdmin]);

    const handleSaveSelfAssessmentPicturesToggle = async (checked: boolean) => {
        setSettings(prev => ({ ...prev, saveSelfAssessmentPictures: checked }));
        setIsSavingPictureSetting(true);
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                const existing = JSON.parse(window.localStorage.getItem('app_settings') || '{}');
                window.localStorage.setItem('app_settings', JSON.stringify({ ...existing, saveSelfAssessmentPictures: checked }));
            } catch {
                // Ignore storage errors
            }
        }
        try {
            await updateAppSettings({ saveSelfAssessmentPictures: checked });
            toast({
                title: checked ? "Self-Assessment Photos Enabled" : "Self-Assessment Photos Disabled",
                description: checked
                    ? "Self-assessment submissions will now save compressed posture photos."
                    : "Self-assessment submissions will now strip posture photos and store metrics only.",
            });
        } catch (error) {
            logger.error('Error updating saveSelfAssessmentPictures setting:', error);
        } finally {
            setIsSavingPictureSetting(false);
        }
    };

    // Handle diagnostic logging toggle
    const handleDiagnosticLoggingToggle = async (checked: boolean) => {
        setIsSavingDiagnosticSetting(true);
        try {
            await updateAppSettings({ diagnosticLoggingEnabled: checked });
            setDiagnosticLoggingEnabled(checked);
            clearSettingsCache();
            toast({
                title: checked ? "Diagnostic Logging Enabled" : "Diagnostic Logging Disabled",
                description: checked
                    ? "Logs are now being captured and sent to the server."
                    : "Diagnostic logging has been turned off.",
            });
        } catch (error) {
            logger.error('Error updating diagnostic logging setting:', error);
            toast({
                variant: "destructive",
                title: "Failed to Update Setting",
                description: "Could not update diagnostic logging setting. Please try again.",
            });
        } finally {
            setIsSavingDiagnosticSetting(false);
        }
    };

    // Handle client log level change
    const handleClientLogLevelChange = async (level: string) => {
        const validatedLevel = level as 'debug' | 'info' | 'warn' | 'error';
        setIsSavingDiagnosticSetting(true);
        try {
            await updateAppSettings({ clientLogLevel: validatedLevel });
            setClientLogLevel(validatedLevel);
            clearSettingsCache();
            toast({
                title: "Client Log Level Updated",
                description: `Logging level set to ${validatedLevel}.`,
            });
        } catch (error) {
            logger.error('Error updating client log level:', error);
            toast({
                variant: "destructive",
                title: "Failed to Update Setting",
                description: "Could not update client log level. Please try again.",
            });
        } finally {
            setIsSavingDiagnosticSetting(false);
        }
    };

    // Handle server log level change
    const handleServerLogLevelChange = async (level: string) => {
        const validatedLevel = level as 'debug' | 'info' | 'warn' | 'error';
        setIsSavingDiagnosticSetting(true);
        try {
            await updateAppSettings({ serverLogLevel: validatedLevel });
            setServerLogLevel(validatedLevel);
            // Clear the cache so the logger picks up the new setting
            clearSettingsCache();
            toast({
                title: "Server Log Level Updated",
                description: `Logging level set to ${validatedLevel}.`,
            });
        } catch (error) {
            logger.error('Error updating server log level:', error);
            toast({
                variant: "destructive",
                title: "Failed to Update Setting",
                description: "Could not update server log level. Please try again.",
            });
        } finally {
            setIsSavingDiagnosticSetting(false);
        }
    };


    return (
        <div className="flex flex-col gap-4 sm:gap-6">
            <SubscriptionBillingCard />

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-primary">Appearance & Theme</CardTitle>
                    <CardDescription className="text-sm">
                        Customize the look and feel. Changes save automatically.
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                    <UnifiedThemeSettings />
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-primary">Measurement Units</CardTitle>
                    <CardDescription className="text-sm">
                        Choose your preferred measurement system for all measurements.
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                    <RadioGroup value={unit} onValueChange={(value) => setUnit(value as 'cm' | 'inches')}>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Label htmlFor="unit-cm" className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border hover:bg-accent transition-colors flex-1">
                                <RadioGroupItem value="cm" id="unit-cm" className="mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-medium text-sm">Metric System</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">Centimeters (cm) & Kilograms (kg)</div>
                                </div>
                            </Label>
                            <Label htmlFor="unit-inches" className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border hover:bg-accent transition-colors flex-1">
                                <RadioGroupItem value="inches" id="unit-inches" className="mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-medium text-sm">Imperial System</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">Inches (in) & Pounds (lbs)</div>
                                </div>
                            </Label>
                        </div>
                    </RadioGroup>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-primary">Self-Assessment Photo Storage</CardTitle>
                    <CardDescription className="text-sm">
                        Configure whether posture photos (base64 captured images) are stored with self-assessment prospect submissions.
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-background">
                        <div className="flex-1 min-w-0">
                            <Label className="text-sm font-medium">
                                Save Self-Assessment Posture Photos
                            </Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                When enabled, posture images are compressed and saved with prospect records. When disabled, images are stripped and only numerical posture metrics are stored.
                            </p>
                        </div>
                        <div className="ml-3 flex-shrink-0">
                            <Switch
                                checked={settings.saveSelfAssessmentPictures !== false}
                                onCheckedChange={handleSaveSelfAssessmentPicturesToggle}
                                disabled={isSavingPictureSetting}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {settings.experimentalFeaturesEnabled && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg text-primary">
                            <Sparkles className="h-4 w-4 text-primary" />
                            Posture Classification
                        </CardTitle>
                        <CardDescription className="text-sm">
                            How Swayback vs Anterior Pelvic Tilt vs Stacked is inferred from your captures. Applies to every client.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="flex flex-col gap-3 rounded-lg border bg-background p-3">
                            <div className="min-w-0">
                                <Label className="text-sm font-medium">Orientation</Label>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    Choose how Swayback vs APT vs Stacked is inferred. ASIS/PSIS pins are optional but improve precision when present.
                                </p>
                            </div>
                            <Select
                                value={settings.postureOrientationStrategyId ?? DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID}
                                onValueChange={(v) =>
                                    setSettings(prev => ({
                                        ...prev,
                                        postureOrientationStrategyId: v as PostureOrientationStrategyId,
                                    }))
                                }
                            >
                                <SelectTrigger className="h-11 w-full text-left" data-testid="posture-orientation-strategy-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {POSTURE_ORIENTATION_STRATEGY_OPTIONS.map((opt) => (
                                        <SelectItem key={opt.id} value={opt.id} className="whitespace-normal" title={opt.label}>
                                            {opt.shortLabel}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg text-primary">
                        <FlaskConical className="h-4 w-4 text-primary" />
                        Experimental Features
                    </CardTitle>
                    <CardDescription className="text-sm">
                        Try early previews like the Agent workspace and Drill Planner. These may change.
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-background">
                        <div className="flex-1 min-w-0">
                            <Label className="text-sm font-medium">
                                Enable Experimental Features
                            </Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Toggle access to in-progress experiences. Applies immediately.
                            </p>
                        </div>
                        <div className="ml-3 flex-shrink-0">
                            <Switch
                                data-testid="experimental-features-switch"
                                checked={settings.experimentalFeaturesEnabled === true}
                                onCheckedChange={(checked) =>
                                    setSettings((prev) => ({
                                        ...prev,
                                        experimentalFeaturesEnabled: checked,
                                        ...(!checked
                                            ? {
                                                  postureOrientationStrategyId:
                                                      DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID,
                                              }
                                            : {}),
                                    }))
                                }
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {isAuthorizedAdmin && (
                <Card className="border-primary/20 bg-primary/5">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg text-primary">
                            <Shield className="h-4 w-4 text-primary" />
                            Admin Mode
                        </CardTitle>
                        <CardDescription className="text-sm">
                            Access and modify assessment logic, parameters, and system configuration.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                        <div className="flex items-center justify-between p-3 border rounded-lg bg-background">
                            <div className="flex-1 min-w-0">
                                <Label className="text-sm font-medium">
                                    Enable Admin Mode
                                </Label>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Admin buttons will appear on assessment tools when enabled.
                                </p>
                            </div>
                            <div className="ml-3 flex-shrink-0">
                                <Switch
                                    checked={isAdminMode}
                                    onCheckedChange={toggleAdminMode}
                                />
                            </div>
                        </div>

                        {isAdminMode && (
                            <div className="p-3 bg-success-surface/30 border border-success rounded-lg">
                                <p className="text-xs text-success-text">
                                    <strong>Admin Mode Active:</strong> Look for primary colored "Config" buttons in tool headers.
                                </p>
                            </div>
                        )}

                        <div className="p-3 border-2 rounded-lg bg-background space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <Label className="text-sm font-medium flex items-center gap-2">
                                        <Bug className="h-4 w-4 text-error-text" />
                                        Diagnostic Logging
                                    </Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        When enabled, captures client logs in the background and flushes to the server periodically. This does not show in the Feedback button. Use the Feedback button to start a user recording when you need to report an issue.
                                    </p>
                                </div>
                                <div className="ml-3 flex-shrink-0">
                                    <Switch
                                        checked={diagnosticLoggingEnabled}
                                        onCheckedChange={handleDiagnosticLoggingToggle}
                                        disabled={isLoadingDiagnosticSetting || isSavingDiagnosticSetting}
                                    />
                                </div>
                            </div>

                            <div className="pl-4 border-l-2 border-muted flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <Label className="text-sm font-medium">Client Log Level</Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Filter logs emitted by the browser.
                                    </p>
                                </div>
                                <div className="ml-3 flex-shrink-0 w-32">
                                    <Select
                                        value={clientLogLevel}
                                        onValueChange={handleClientLogLevelChange}
                                        disabled={isLoadingDiagnosticSetting || isSavingDiagnosticSetting || !diagnosticLoggingEnabled}
                                    >
                                        <SelectTrigger className="h-8">
                                            <SelectValue placeholder="Select level" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="debug">Debug</SelectItem>
                                            <SelectItem value="info">Info</SelectItem>
                                            <SelectItem value="warn">Warning</SelectItem>
                                            <SelectItem value="error">Error</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="pl-4 border-l-2 border-muted flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <Label className="text-sm font-medium">Server Log Level</Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Filter logs emitted by the server.
                                    </p>
                                </div>
                                <div className="ml-3 flex-shrink-0 w-32">
                                    <Select
                                        value={serverLogLevel}
                                        onValueChange={handleServerLogLevelChange}
                                        disabled={isLoadingDiagnosticSetting || isSavingDiagnosticSetting || !diagnosticLoggingEnabled}
                                    >
                                        <SelectTrigger className="h-8">
                                            <SelectValue placeholder="Select level" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="debug">Debug</SelectItem>
                                            <SelectItem value="info">Info</SelectItem>
                                            <SelectItem value="warn">Warning</SelectItem>
                                            <SelectItem value="error">Error</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {diagnosticLoggingEnabled && (
                                <div className="p-3 bg-warning-surface/30 border border-warning rounded-lg">
                                    <p className="text-xs text-warning-text">
                                        <strong>Diagnostic Logging Active:</strong> Background log capture is active. Logs are stored locally and flushed to the server periodically. Use the Feedback button to capture logs for a GitHub issue.
                                    </p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-primary">Tooltips & Help</CardTitle>
                    <CardDescription className="text-sm">
                        Control when helpful tips and guidance are shown.
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-background">
                        <div className="flex-1 min-w-0">
                            <Label className="text-sm font-medium">
                                Show Posture Analysis Tips
                            </Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Display photography tips when starting posture capture.
                            </p>
                        </div>
                        <div className="ml-3 flex-shrink-0">
                            <Switch
                                checked={settings.showPostureAnalysisTips !== false}
                                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showPostureAnalysisTips: checked }))}
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-background">
                        <div className="flex-1 min-w-0">
                            <Label htmlFor="show-remote-assessment-steps" className="text-sm font-medium">
                                Show Remote Assessment steps
                            </Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Show the &quot;what to do now&quot; steps above the Remote Assessment workbench.
                            </p>
                        </div>
                        <div className="ml-3 flex-shrink-0">
                            <Switch
                                id="show-remote-assessment-steps"
                                aria-label="Show Remote Assessment steps"
                                checked={settings.showRemoteAssessmentSteps !== false}
                                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showRemoteAssessmentSteps: checked }))}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-primary">SoP Assessment Mode</CardTitle>
                    <CardDescription className="text-sm">
                        Choose whether SoP uses the current or legacy assessment model.
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-background">
                        <div className="flex-1 min-w-0">
                            <Label className="text-sm font-medium">
                                Use Legacy SoP Assessment (pre-March 2026)
                            </Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Switches to the previous SoP table and RuleDocs, including Ober&apos;s hip test behavior.
                            </p>
                        </div>
                        <div className="ml-3 flex-shrink-0">
                            <Switch
                                checked={settings.useLegacySopAssessment === true}
                                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, useLegacySopAssessment: checked }))}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <footer className="text-xs text-muted-foreground mt-4 pt-4 border-t">
                <p>© 2026 Harry - Your Biomechanics Assistant. All Rights Reserved.</p>
                <p className="mt-1">This tool provides a simulated analysis for educational purposes and is not a substitute for professional medical advice.</p>
            </footer>
        </div>
    );
}


